// Copyright (c) 2026 Yunus YILDIZ — SPDX-License-Identifier: BUSL-1.1

import * as Radix from '@radix-ui/react-dropdown-menu'
import { Check, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import { type ReactNode } from 'react'

export const DropdownMenu = Radix.Root
export const DropdownMenuTrigger = Radix.Trigger

interface DropdownMenuContentProps extends Radix.DropdownMenuContentProps {
  className?: string
}

export function DropdownMenuContent({ className, children, ...props }: DropdownMenuContentProps) {
  return (
    <Radix.Portal>
      <Radix.Content
        className={cn(
          'z-50 min-w-[160px] rounded-lg border border-line bg-raised p-1 shadow-lg',
          'animate-slide-up data-[side=top]:animate-slide-up data-[state=open]:animate-fade-in',
          className
        )}
        sideOffset={4}
        {...props}
      >
        {children}
      </Radix.Content>
    </Radix.Portal>
  )
}

interface DropdownMenuItemProps extends Radix.DropdownMenuItemProps {
  icon?: ReactNode
  destructive?: boolean
}

export function DropdownMenuItem({ className, icon, destructive, children, ...props }: DropdownMenuItemProps) {
  return (
    <Radix.Item
      className={cn(
        'flex cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none',
        'transition-colors focus:bg-surface',
        destructive ? 'text-danger focus:bg-danger-subtle' : 'text-ink',
        props.disabled && 'opacity-50',
        className
      )}
      {...props}
    >
      {icon && <span className="h-4 w-4 shrink-0">{icon}</span>}
      {children}
    </Radix.Item>
  )
}

export function DropdownMenuSeparator() {
  return <Radix.Separator className="my-1 h-px bg-line" />
}

export function DropdownMenuLabel({ children }: { children: ReactNode }) {
  return <Radix.Label className="px-2 py-1 text-xs font-semibold text-ink-muted">{children}</Radix.Label>
}

export { Check, ChevronRight }
